import { buildDemoSnapshot, formatCurrency, getCurrentScenarioStep, getScenarioCaption } from './model.ts';
import type { ActiveQuoteLine, DemoPersistedState, DemoSnapshot, FlowState, WorkOrder } from './types.ts';
import type { MvpPilotState, MvpQuoteLineRecord, MvpWorkOrderRecord } from './mvpApi.ts';

function toFlowState(state: MvpPilotState): FlowState {
  return getFlowStateForWorkOrder(state, state.focusWorkOrderId);
}

export function getFlowStateForWorkOrder(state: MvpPilotState, workOrderId: string): FlowState {
  const focusWorkOrder = state.workOrders.find((item) => item.id === workOrderId) ?? null;
  const focusReport = state.reports.find((item) => item.workOrderId === workOrderId) ?? null;
  const focusInvoice = state.invoices.find((item) => item.workOrderId === workOrderId) ?? null;

  return {
    quoteConverted: Boolean(focusWorkOrder),
    onSite: focusWorkOrder ? focusWorkOrder.status !== 'ready_dispatch' : false,
    photosCaptured: focusReport?.photosCaptured ?? false,
    voiceCaptured: focusReport?.voiceCaptured ?? false,
    signatureCaptured: focusReport?.signatureCaptured ?? false,
    reportStatus: focusReport?.status ?? 'draft',
    rejectionReason: focusReport?.rejectionReason ?? null,
    syncStatus: focusReport?.syncStatus ?? 'pending',
    syncConflictReason: focusReport?.syncConflictReason ?? null,
    invoiceCreated: Boolean(focusInvoice),
  };
}

function toPersistedState(state: MvpPilotState): DemoPersistedState {
  const focusWorkOrder = state.workOrders.find((item) => item.id === state.focusWorkOrderId) ?? null;

  return {
    version: 1,
    quoteHasProbeCheck: state.quoteLines.some((item) => item.quoteId === state.focusQuoteId && item.optionalFlag),
    dispatchAssignment: focusWorkOrder
      ? {
          ownerName: focusWorkOrder.ownerName,
          scheduledWindow: focusWorkOrder.scheduledWindow,
        }
      : null,
    flowState: toFlowState(state),
  };
}

function toActiveQuoteLines(lines: MvpQuoteLineRecord[]): ActiveQuoteLine[] {
  return lines.map((line) => ({
    ...line,
    unitLabel: formatCurrency(line.unitCents),
    totalCents: line.quantity * line.unitCents,
    totalLabel: formatCurrency(line.quantity * line.unitCents),
  }));
}

export function getQuoteDetailFromPilotState(state: MvpPilotState, quoteId: string): {
  lines: ActiveQuoteLine[];
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
  convertedWorkOrderId: string | null;
} | null {
  const quote = state.quotes.find((item) => item.id === quoteId);
  if (!quote) return null;
  const lines = toActiveQuoteLines(state.quoteLines.filter((item) => item.quoteId === quoteId));
  const subtotalCents = lines.reduce((sum, line) => sum + line.totalCents, 0);
  const vatCents = Math.round(subtotalCents * 0.2);
  const totalCents = subtotalCents + vatCents;
  const convertedWorkOrderId = state.workOrders.find((item) => item.quoteId === quoteId)?.id ?? null;
  return {
    lines,
    subtotalCents,
    vatCents,
    totalCents,
    convertedWorkOrderId,
  };
}

export function getWorkOrderViewFromPilotState(state: MvpPilotState, workOrderId: string): WorkOrder | null {
  const workOrder = state.workOrders.find((item) => item.id === workOrderId);
  if (!workOrder) return null;
  return toWorkOrderView(state, workOrder);
}

function getWorkOrderStatusLabel(status: string, reportStatus: FlowState['reportStatus'] | null, hasInvoice: boolean): string {
  if (hasInvoice || status === 'invoiced') return 'Facturé';
  switch (status) {
    case 'ready_dispatch':
      return 'Affectation confirmée';
    case 'on_site':
      return 'Sur site';
    case 'in_review':
      return reportStatus === 'resubmitted' ? 'Re-soumis' : 'En validation';
    case 'rework_required':
      return 'Reprise requise';
    case 'ready_to_invoice':
      return 'Prêt à facturer';
    default:
      return status;
  }
}

function toWorkOrderView(state: MvpPilotState, workOrder: MvpWorkOrderRecord): WorkOrder {
  const checklist = state.checklistItems
    .filter((item) => item.workOrderId === workOrder.id)
    .map((item) => ({ label: item.label, done: item.done }));
  const proof = [...new Set(state.proofs.filter((item) => item.workOrderId === workOrder.id).map((item) => item.label))];
  const timeline = state.events
    .filter((item) => item.workOrderId === workOrder.id)
    .map((item) => ({ at: item.at, label: item.label }))
    .sort((left, right) => left.at.localeCompare(right.at));
  const report = state.reports.find((item) => item.workOrderId === workOrder.id) ?? null;
  const invoice = state.invoices.find((item) => item.workOrderId === workOrder.id) ?? null;

  return {
    id: workOrder.id,
    title: workOrder.title,
    client: workOrder.clientName,
    site: workOrder.siteName,
    status: getWorkOrderStatusLabel(workOrder.status, report?.status ?? null, Boolean(invoice)),
    window: workOrder.scheduledWindow,
    owner: workOrder.ownerName,
    checklist,
    proof,
    timeline,
    revenue: `${formatCurrency(workOrder.revenueCents)} HT`,
    invoiceStatus: invoice?.invoiceNumber ?? workOrder.invoiceStatusLabel,
    nextAction: workOrder.nextAction,
    upsell: workOrder.upsell,
    voiceDraft: report?.voiceDraft ?? '',
    invoiceAction: workOrder.invoiceAction,
  };
}

export function snapshotFromMvpPilotState(state: MvpPilotState): DemoSnapshot {
  const persistedState = toPersistedState(state);
  const baseSnapshot = buildDemoSnapshot(persistedState);
  const focusQuoteLines = state.quoteLines.filter((item) => item.quoteId === state.focusQuoteId);
  const activeQuoteLines = toActiveQuoteLines(focusQuoteLines);
  const subtotalCents = activeQuoteLines.reduce((sum, line) => sum + line.totalCents, 0);
  const vatCents = Math.round(subtotalCents * 0.2);
  const totalCents = subtotalCents + vatCents;
  const currentScenarioStep = getCurrentScenarioStep(persistedState.flowState);

  return {
    ...baseSnapshot,
    workOrders: state.workOrders.map((item) => toWorkOrderView(state, item)),
    activeQuoteLines: activeQuoteLines.length > 0 ? activeQuoteLines : baseSnapshot.activeQuoteLines,
    subtotalCents: activeQuoteLines.length > 0 ? subtotalCents : baseSnapshot.subtotalCents,
    vatCents: activeQuoteLines.length > 0 ? vatCents : baseSnapshot.vatCents,
    totalCents: activeQuoteLines.length > 0 ? totalCents : baseSnapshot.totalCents,
    flowState: persistedState.flowState,
    quoteHasProbeCheck: persistedState.quoteHasProbeCheck,
    dispatchAssignment: persistedState.dispatchAssignment,
    currentScenarioStep,
    currentScenarioCaption: getScenarioCaption(currentScenarioStep),
  };
}
